import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug, getPopularArticles } from '@/mocks/articles';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
        <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-200">
          <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
        </div>
        <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">文章未找到</h1>
        <p className="text-foreground-600 mb-8">您访问的文章不存在或已被移除。</p>
        <Link
          to="/"
          className="inline-flex items-center px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
        >
          <i className="ri-arrow-left-line mr-2"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const popularArticles = getPopularArticles(4).filter((a) => a.id !== article.id);

  const baseUrl = 'https://goldroute.github.io';

  return (
    <>
      <SEO
        title={`${article.title} - jm天堂官方指南`}
        description={article.excerpt}
        keywords={`jm天堂,${article.tags.join(',')},${article.categoryName}`}
        canonicalUrl={`${baseUrl}/article/${article.slug}`}
        ogType="article"
        ogImage={article.imageUrl}
      />
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          image: article.imageUrl,
          author: {
            '@type': 'Organization',
            name: article.author,
          },
          datePublished: article.publishDate,
          dateModified: article.updateDate,
          publisher: {
            '@type': 'Organization',
            name: 'jm天堂',
            url: baseUrl,
          },
        }}
      />

      <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: article.categoryName, href: `/category/${article.categoryId}` },
            { label: article.title },
          ]}
        />

        <header className="mb-10">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="text-xs font-medium text-primary-600 bg-primary-50 px-3 py-1.5 rounded-full">
              {article.categoryName}
            </span>
            <span className="text-xs text-foreground-500 flex items-center">
              <i className="ri-time-line mr-1"></i>
              {article.readTime}
            </span>
            <span className="text-xs text-foreground-500 flex items-center">
              <i className="ri-eye-line mr-1"></i>
              {article.viewCount.toLocaleString()} 次阅读
            </span>
          </div>

          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
            {article.title}
          </h1>

          <div className="flex items-center space-x-4 text-sm text-foreground-500">
            <span>发布于 {article.publishDate}</span>
            <span>更新于 {article.updateDate}</span>
            <span>作者：{article.author}</span>
          </div>
        </header>

        <div className="mb-10 rounded-xl overflow-hidden aspect-[21/9] bg-background-200">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>

        <div
          className="prose prose-lg max-w-none
            prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-bold
            prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-5
            prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
            prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5
            prose-strong:text-foreground-900 prose-strong:font-semibold
            prose-li:text-foreground-700 prose-li:leading-relaxed
            prose-ol:my-5 prose-ul:my-5
            prose-li:my-1.5 prose-li:pl-1
            [&_ol]:list-decimal [&_ol]:pl-6
            [&_ul]:list-disc [&_ul]:pl-6
            prose-a:text-primary-600 prose-a:no-underline hover:prose-a:text-primary-700
          "
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        <div className="mt-10 pt-6 border-t border-background-200/70">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-foreground-500 mr-2">标签：</span>
            {article.tags.map((tag) => (
              <Link
                key={tag}
                to={`/category/${article.categoryId}`}
                className="inline-flex items-center px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 text-xs hover:bg-primary-50 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                {tag}
              </Link>
            ))}
          </div>
        </div>
      </article>

      {popularArticles.length > 0 && (
        <section className="bg-background-100 py-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
              热门文章推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {popularArticles.map((item) => (
                <Link
                  key={item.id}
                  to={`/article/${item.slug}`}
                  className="group flex items-start space-x-4 bg-background-50 rounded-xl p-4 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2 mb-1.5">
                      {item.title}
                    </h3>
                    <p className="text-xs text-foreground-500">{item.publishDate} · {item.readTime}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}